/**
 * Sentry + LogRocket bootstrap.
 *
 * Imported first (before any other module) from index.js so the SDKs
 * register their global hooks (fetch/xhr, unhandledrejection) before
 * any app code has a chance to run.
 *
 * Env vars (CRA → must be prefixed REACT_APP_):
 *   REACT_APP_SENTRY_DSN              — public client key for the React SDK
 *   REACT_APP_SENTRY_ENVIRONMENT      — "production" | "preview" | "dev"
 *   REACT_APP_LOGROCKET_APP_ID        — e.g. "abcd1/central-rentals"
 */

import * as Sentry from "@sentry/react";
import LogRocket from "logrocket";
import setupLogRocketReact from "logrocket-react";

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const SENTRY_ENV = process.env.REACT_APP_SENTRY_ENVIRONMENT || "production";
const LOGROCKET_APP_ID = process.env.REACT_APP_LOGROCKET_APP_ID;
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// ---------------------------------------------------------------------------
// LogRocket — client-only session replay. Init first so LogRocket can
// intercept the session before Sentry tags start firing.
// ---------------------------------------------------------------------------
if (LOGROCKET_APP_ID && typeof window !== "undefined") {
  try {
    LogRocket.init(LOGROCKET_APP_ID, {
      // Redact sensitive form fields by default — most auth flows drop the
      // password value into an <input type=password>. LogRocket masks these
      // automatically but we set an explicit sanitizer for extra safety on
      // any custom text inputs marked with the `.private` class.
      dom: { inputSanitizer: true },
      network: {
        requestSanitizer: (request) => {
          // Never send Authorization headers to LogRocket
          if (request.headers && request.headers.Authorization) {
            request.headers.Authorization = "";
          }
          if (request.headers && request.headers.authorization) {
            request.headers.authorization = "";
          }
          return request;
        },
      },
    });
    setupLogRocketReact(LogRocket);
  } catch (e) {
    console.warn("[logrocket] init failed", e);
  }
}

// ---------------------------------------------------------------------------
// Sentry — browser SDK with browser-tracing + session replay. Every event
// is tagged with the LogRocket session URL so we can jump from a Sentry
// error to the exact replay in one click.
// ---------------------------------------------------------------------------
if (SENTRY_DSN) {
  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: SENTRY_ENV,
      // Keep sample rates modest in prod. Bump to 1.0 during a live
      // incident by editing REACT_APP env vars and redeploying.
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
      ],
      tracePropagationTargets: [
        "localhost",
        /^\/api\//,
        ...(API_BASE ? [new RegExp(`^${API_BASE.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}`)] : []),
      ],
      // Attach the LogRocket session URL to every outgoing Sentry event.
      beforeSend(event) {
        try {
          const sessionURL = LogRocket && LogRocket.sessionURL;
          if (sessionURL) {
            event.extra = { ...(event.extra || {}), logrocket_session_url: sessionURL };
          }
        } catch { /* noop */ }
        return event;
      },
    });

    // Also register the session URL as a searchable tag once LogRocket
    // has established the session (this happens shortly after init).
    if (LOGROCKET_APP_ID) {
      try {
        LogRocket.getSessionURL((sessionURL) => {
          Sentry.setTag("logrocket_session_url", sessionURL);
          Sentry.setContext("logrocket", { sessionURL });
        });
      } catch { /* noop */ }
    }
  } catch (e) {
    console.warn("[sentry] init failed", e);
  }
}

// Expose a tiny helper for other modules (auth, etc.) to link a
// logged-in user to both Sentry and LogRocket.
export function identifyUser({ id, email, name } = {}) {
  if (!id && !email) return;
  try {
    Sentry.setUser({ id, email, username: name });
  } catch { /* noop */ }
  try {
    if (LOGROCKET_APP_ID) {
      LogRocket.identify(id || email, {
        name: name || "",
        email: email || "",
      });
    }
  } catch { /* noop */ }
}

export { Sentry, LogRocket };
